import * as React from "react";
import { Error } from "@v2/shared/ui/custom/error.ui";
import { PaywallError } from "@trello-powerup-monorepo/iframe-messenger-paywall-v2/parent";

interface PaywallErrorBoundaryState {
  hasError: boolean;
  error: PaywallError | null;
}

interface PaywallErrorBoundaryProps {
  children: React.ReactNode;
  onError?: (error: PaywallError, errorInfo: React.ErrorInfo) => void;
}

export class PaywallErrorBoundary extends React.Component<
  PaywallErrorBoundaryProps,
  PaywallErrorBoundaryState
> {
  constructor(props: PaywallErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(
    error: Error
  ): Partial<PaywallErrorBoundaryState> {
    // Only handle PaywallError instances
    if (error instanceof PaywallError) {
      return {
        hasError: true,
        error: error,
      };
    }

    // Re-throw non-PaywallError instances to let them bubble up
    throw error;
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (error instanceof PaywallError && this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError && this.state.error) {
      return (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "var(--background-color, #fdf9f6)",
            gap: "1rem",
          }}
        >
          <Error error={this.state.error.userMessage} />

          {/* AIDEV-NOTE: Developer info for debugging, remove in production */}
          {process.env.NODE_ENV === "development" && (
            <details
              style={{ marginTop: "1rem", fontSize: "12px", color: "#666" }}
            >
              <summary>Debug Info</summary>
              <pre style={{ marginTop: "0.5rem", fontSize: "11px" }}>
                {JSON.stringify(
                  {
                    code: this.state.error.code,
                    message: this.state.error.message,
                  },
                  null,
                  2
                )}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}
